// ===============================================================
// ProBank — Login
//
// Dark, premium banking look with the shared video background
// (same as the landing page):
//  - Password login (always available)
//  - Biometric (passkey) login — fingerprint / face on the device
//  - After password login, first-time users get an "Enable
//    biometric?" prompt; enrolled users get a one-tap biometric
//    button on every later visit (email remembered locally)
//  - If biometric fails (dead passkey), auto-reset and re-enable
//  - After ANY successful login, the device is subscribed to push
//    notifications (transaction alerts with sound on the phone)
// ===============================================================

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  login,
  startRegistration,
  verifyRegistration,
  startAuthentication,
  verifyAuthentication,
  resetBiometric,
} from "../utils/api";
import {
  startAuthentication as browserStartAuthentication,
  startRegistration as browserStartRegistration,
  browserSupportsWebAuthn,
} from "@simplewebauthn/browser";
import { useCountry } from "../context/CountryContext";
import { subscribeUserToPush } from "../utils/pushSubscribe";

const LAST_EMAIL_KEY = "pb_last_email";
const BIO_ENABLED_KEY = "pb_bio_enabled";

const getBioMap = () => {
  try { return JSON.parse(localStorage.getItem(BIO_ENABLED_KEY) || "{}"); }
  catch { return {}; }
};

// Native fingerprint bridge — injected by the ProBank APK (v1.3+).
// Returns the bridge object when running inside the app, else null.
const nativeBridge = () => {
  try {
    const b = window.ProBankNative;
    if (b && typeof b.isAvailable === "function" && b.isAvailable()) return b;
  } catch { /* not inside the app */ }
  return null;
};

const sanitizeRegOptions = (options) => ({
  ...options,
  challenge: String(options?.challenge || ""),
  user: {
    ...(options?.user || {}),
    id: String(options?.user?.id || ""),
    name: String(options?.user?.name || ""),
    displayName: String(options?.user?.displayName || options?.user?.name || ""),
  },
  rp: {
    ...(options?.rp || {}),
    id: String(options?.rp?.id || ""),
    name: String(options?.rp?.name || ""),
  },
  pubKeyCredParams: Array.isArray(options?.pubKeyCredParams) ? options.pubKeyCredParams : [],
  excludeCredentials: Array.isArray(options?.excludeCredentials) ? options.excludeCredentials : [],
  hints: Array.isArray(options?.hints) ? options.hints : [],
  attestationFormats: Array.isArray(options?.attestationFormats) ? options.attestationFormats : [],
});

const sanitizeAuthOptions = (options) => ({
  ...options,
  challenge: String(options?.challenge || ""),
  allowCredentials: Array.isArray(options?.allowCredentials) ? options.allowCredentials : [],
  hints: Array.isArray(options?.hints) ? options.hints : [],
});

export default function Login() {
  const { isGhana, config, resetCountry, setCountry } = useCountry();

  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [showEnablePrompt, setShowEnablePrompt] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bioLoading, setBioLoading] = useState(false);
  const [wrongCountryModal, setWrongCountryModal] = useState(null);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const [bioReady, setBioReady] = useState(false);
  // WebAuthn (fingerprint/face) needs a full browser — Android WebView
  // (the APK) does not expose it, so hide all biometric UI there.
  const [webAuthnOk] = useState(() => {
    try { return browserSupportsWebAuthn(); } catch { return false; }
  });
  // True when the APK has a saved session unlockable by fingerprint
  const [nativeBio, setNativeBio] = useState(false);
  const navigate = useNavigate();

  const country = isGhana
    ? { code: "GH", name: config.name }
    : { code: "NG", name: config.name };

  useEffect(() => {
    const lastEmail = localStorage.getItem(LAST_EMAIL_KEY) || "";
    if (lastEmail) {
      setForm((f) => ({ ...f, email: lastEmail }));
      setBioReady(!!getBioMap()[lastEmail]);
    }
    const b = nativeBridge();
    if (b) {
      try { setNativeBio(!!b.hasSavedSession()); } catch { /* ignore */ }
    }
  }, []);

  // The APK calls this after a successful fingerprint scan
  useEffect(() => {
    window.__probankNativeUnlock = (json) => {
      try {
        const data = JSON.parse(json);
        if (!data?.token || !data?.user) return;
        saveSession(data);
        subscribePushInBackground(data.user);
        navigate(redirectToPlanDashboard(data.user));
      } catch { /* malformed payload — ignore */ }
    };
    return () => { try { delete window.__probankNativeUnlock; } catch { /* ignore */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    const email = form.email.trim().toLowerCase();
    setBioReady(!!email && !!getBioMap()[email]);
  }, [form.email]);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const redirectToPlanDashboard = (user) => {
    if (!user?.subscriptionActive || !user?.plan) return "/dashboard-demo";
    switch (user.plan.toLowerCase()) {
      case "basic": return "/dashboard-basic";
      case "premium": return "/dashboard-premium";
      case "elite": return "/dashboard-elite";
      default: return "/dashboard-demo";
    }
  };

  const saveSession = (data) => {
    localStorage.setItem("token", data.token);
    localStorage.setItem("user", JSON.stringify(data.user));
    localStorage.setItem("userId", data.user.id || data.user._id);
    localStorage.setItem(LAST_EMAIL_KEY, data.user.email);
  };

  const markBioEnabled = (email) => {
    const map = getBioMap();
    map[email.trim().toLowerCase()] = true;
    localStorage.setItem(BIO_ENABLED_KEY, JSON.stringify(map));
  };

  const clearBioEnabled = (email) => {
    const map = getBioMap();
    delete map[email.trim().toLowerCase()];
    localStorage.setItem(BIO_ENABLED_KEY, JSON.stringify(map));
  };

  // Subscribe this device to push notifications after login.
  // Fire-and-forget — never blocks navigation, never breaks login.
  const subscribePushInBackground = (user) => {
    const userId = user?.id || user?._id;
    if (!userId) return;
    subscribeUserToPush(userId).catch(() => {});
  };

  // ---------------------------------------------------------------
  // Password login
  // ---------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setRejectionReason("");
    setLoading(true);

    try {
      const res = await login({
        email: form.email,
        password: form.password,
        country: country.code,
      });
      const data = res.data;

      if (data?.token) {
        saveSession(data);

        // Subscribe phone for transaction notifications (with sound).
        subscribePushInBackground(data.user);

        const bridge = nativeBridge();
        if (bridge) {
          // Inside the APK — remember the session for fingerprint unlock
          try {
            bridge.saveSession(
              data.token,
              JSON.stringify(data.user),
              String(data.user.id || data.user._id || "")
            );
            setNativeBio(true);
          } catch { /* saving is best-effort */ }
          navigate(redirectToPlanDashboard(data.user));
        } else if (!webAuthnOk) {
          // No passkey support here — go straight in
          navigate(redirectToPlanDashboard(data.user));
        } else if (!data.user.passkey?.credentialID) {
          setShowEnablePrompt(true);
        } else {
          markBioEnabled(data.user.email);
          navigate(redirectToPlanDashboard(data.user));
        }
      }
    } catch (err) {
      const errData = err.response?.data || {};
      if (errData.wrongCountry) {
        setWrongCountryModal({
          accountCountry: errData.accountCountry,
          accountCountryName: errData.accountCountryName,
          message: errData.message,
        });
      } else {
        setError(errData.message || "Login failed");
        if (errData.rejectionReason) setRejectionReason(errData.rejectionReason);
      }
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------
  // Enable biometrics (post-login prompt)
  // ---------------------------------------------------------------
  const handleEnableBiometrics = async () => {
    setError("");
    setBioLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const email = user?.email;
      if (!email) throw new Error("Please log in again.");

      const { data: options } = await startRegistration(email);

      if (options?.alreadyEnabled || options?.message?.includes("already enabled")) {
        markBioEnabled(email);
        return navigate(redirectToPlanDashboard(user));
      }
      if (!options?.challenge) throw new Error("Could not start biometric enrollment.");

      const attResp = await browserStartRegistration({ optionsJSON: sanitizeRegOptions(options) });
      await verifyRegistration(email, attResp);

      markBioEnabled(email);
      navigate(redirectToPlanDashboard(user));
    } catch (err) {
      if (err?.name === "NotAllowedError") {
        const user = JSON.parse(localStorage.getItem("user"));
        return navigate(redirectToPlanDashboard(user));
      }
      setError(err.response?.data?.message || err.message || "Could not enable biometrics.");
    } finally {
      setBioLoading(false);
    }
  };

  const handleSkipEnable = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    navigate(redirectToPlanDashboard(user));
  };

  // ---------------------------------------------------------------
  // Biometric login — with auto-reset on dead passkey
  // ---------------------------------------------------------------
  const handleBiometricLogin = async () => {
    setError("");
    // Inside the APK — ask Android for the fingerprint; on success the
    // app calls window.__probankNativeUnlock (registered above).
    const bridge = nativeBridge();
    if (bridge && nativeBio) {
      try { bridge.unlock(); } catch { setError("Fingerprint is not available on this device. Please use your password."); }
      return;
    }
    setBioLoading(true);
    try {
      const email = (form.email || localStorage.getItem(LAST_EMAIL_KEY) || "").trim();
      if (!email) throw new Error("Enter your email first");

      const { data: options } = await startAuthentication(email);
      if (!options?.challenge) throw new Error("Biometric login is not enabled for this account");

      const authResp = await browserStartAuthentication({ optionsJSON: sanitizeAuthOptions(options) });
      const { data } = await verifyAuthentication(email, authResp, { country: country.code });

      if (data.wrongCountry) {
        setWrongCountryModal({
          accountCountry: data.accountCountry,
          accountCountryName: data.accountCountryName,
          message: data.message,
        });
        return;
      }

      saveSession(data);
      markBioEnabled(data.user.email);

      // Subscribe phone for transaction notifications (with sound).
      subscribePushInBackground(data.user);

      navigate(redirectToPlanDashboard(data.user));
    } catch (err) {
      // Dead passkey — device has no credential for this domain
      if (err?.name === "NotAllowedError") {
        const email = (form.email || localStorage.getItem(LAST_EMAIL_KEY) || "").trim();

        // Auto-reset the dead passkey so user can re-enable
        try {
          await resetBiometric(email);
          clearBioEnabled(email);
          setBioReady(false);
          setError("Biometric login was reset. Please log in with your password to set it up again on this device.");
        } catch (resetErr) {
          setError("Biometric login failed. Please use your password.");
        }
        return;
      }

      const errData = err.response?.data || {};
      if (errData.wrongCountry) {
        setWrongCountryModal({
          accountCountry: errData.accountCountry,
          accountCountryName: errData.accountCountryName,
          message: errData.message,
        });
        return;
      }
      setError(errData.message || err.message || "Biometric login failed. Please use your password instead.");
    } finally {
      setBioLoading(false);
    }
  };

  const handleSwitchToCorrectCountry = () => {
    if (wrongCountryModal?.accountCountry) {
      setCountry(wrongCountryModal.accountCountry);
      setWrongCountryModal(null);
    }
  };

  const handleConfirmSwitchCountry = () => {
    resetCountry();
    navigate("/");
    setShowSwitchConfirm(false);
  };

  // ---------------------------------------------------------------
  // Styles — dark premium banking look
  // ---------------------------------------------------------------
  const C = {
    bg: "#0B0D10",
    card: "#14171C",
    cardSoft: "#1A1E25",
    border: "rgba(255,255,255,0.08)",
    gold: "#C7A472",
    goldDeep: "#B8935F",
    text: "#EDEAE3",
    muted: "#8B8D93",
  };

  const S = {
    page: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px 16px",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
      color: C.text,
      position: "relative",
      overflow: "hidden",
    },
    card: {
      width: "100%",
      maxWidth: 420,
      background: "rgba(20,23,28,0.88)",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
      borderRadius: 18,
      border: `1px solid ${C.border}`,
      padding: "34px 26px",
      boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
      position: "relative",
      zIndex: 10,
    },
    label: {
      display: "block",
      fontSize: 11,
      fontWeight: 600,
      color: C.muted,
      marginBottom: 7,
      letterSpacing: "0.14em",
      textTransform: "uppercase",
    },
    input: {
      width: "100%",
      padding: "13px 14px",
      borderRadius: 10,
      border: `1px solid ${C.border}`,
      background: "rgba(11,13,16,0.85)",
      color: C.text,
      fontSize: 15,
      outline: "none",
      boxSizing: "border-box",
    },
    primaryBtn: {
      width: "100%",
      padding: "14px",
      borderRadius: 10,
      border: "none",
      background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldDeep} 100%)`,
      color: "#0B0D10",
      fontWeight: 700,
      fontSize: 15,
      cursor: "pointer",
    },
    bioBtn: {
      width: "100%",
      padding: "14px",
      borderRadius: 10,
      border: `1px solid ${C.goldDeep}`,
      background: "rgba(184,147,95,0.08)",
      color: C.gold,
      fontWeight: 700,
      fontSize: 15,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
    },
    ghostBtn: {
      width: "100%",
      padding: "12px",
      borderRadius: 10,
      border: `1px solid ${C.border}`,
      background: "transparent",
      color: C.muted,
      fontWeight: 600,
      fontSize: 14,
      cursor: "pointer",
    },
    sheet: {
      width: "100%",
      maxWidth: 440,
      background: C.card,
      border: `1px solid ${C.border}`,
      borderRadius: "18px 18px 0 0",
      padding: "26px 24px 34px",
    },
  };

  const FingerprintIcon = ({ size = 22, color = C.gold }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" style={{ width: size, height: size }}>
      <path d="M12 11c1.66 0 3 1.34 3 3 0 2.5-.5 4.5-1.2 6" />
      <path d="M9.2 12.2c-.13.6-.2 1.2-.2 1.8 0 2.1.4 3.9 1.1 5.6" />
      <path d="M12 8c3.31 0 6 2.69 6 6 0 1.7-.15 3.4-.45 5" />
      <path d="M6.3 10.3C7 8.4 8.4 7 10.5 6.5" />
      <path d="M5 13.4c.02.7 0 1.4-.06 2.1" />
      <path d="M18.6 8.6C17.2 5.9 14.8 4.5 12 4.5c-1.5 0-2.9.4-4.1 1.1" />
    </svg>
  );

  return (
    <div style={S.page}>
      {/* Sticky background — same as the landing page */}
      <div style={{ position: "fixed", inset: 0, zIndex: -1, overflow: "hidden", background: C.bg }}>
        <img
          src="/hero-bg.jpg"
          alt=""
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
        />
        <div style={{ position: "absolute", inset: 0, background: "rgba(11,13,16,0.85)" }} />
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 900px 500px at 85% 0%, rgba(184,147,95,0.10), transparent 60%)" }} />
      </div>

      {/* Country badge */}
      <div style={{ position: "fixed", top: 16, right: 16, zIndex: 20, fontSize: 11, fontWeight: 600, color: C.muted, background: "rgba(20,23,28,0.8)", border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 12px", letterSpacing: "0.08em", backdropFilter: "blur(6px)" }}>
        {country.code} · {country.name}
      </div>

      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} style={S.card}>

        {/* Brand */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{
            width: 54, height: 54, margin: "0 auto 16px", borderRadius: 16,
            background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldDeep} 100%)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#0B0D10", fontWeight: 800, fontSize: 22,
            boxShadow: "0 8px 24px rgba(184,147,95,0.25)",
          }}>
            P
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.01em" }}>
            Welcome back
          </div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 5 }}>Log in to your ProBank account</div>
        </div>

        {error && (
          <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5", borderRadius: 10, padding: "10px 14px", fontSize: 13, marginBottom: 16, wordBreak: "break-word" }}>
            {error}
          </div>
        )}

        {rejectionReason && (
          <div style={{ background: "rgba(184,147,95,0.08)", border: "1px solid rgba(184,147,95,0.3)", color: C.gold, borderRadius: 10, padding: "10px 14px", fontSize: 13, marginBottom: 16 }}>
            <strong>Reason:</strong> {rejectionReason}
          </div>
        )}

        <AnimatePresence mode="wait">
          {showEnablePrompt ? (
            <motion.div key="enablePrompt" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} style={{ textAlign: "center" }}>
              <div style={{
                width: 66, height: 66, margin: "0 auto 18px", borderRadius: "50%",
                background: "rgba(184,147,95,0.1)", border: "1px solid rgba(184,147,95,0.3)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <FingerprintIcon size={30} />
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>Enable biometric login?</div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.65, marginBottom: 24 }}>
                Next time, log in instantly with your fingerprint or face, no password needed. You can always use your password as backup.
              </div>
              <button onClick={handleEnableBiometrics} disabled={bioLoading} style={{ ...S.primaryBtn, marginBottom: 10, opacity: bioLoading ? 0.7 : 1 }}>
                {bioLoading ? "Waiting for your device…" : "Enable fingerprint / face"}
              </button>
              <button onClick={handleSkipEnable} style={S.ghostBtn}>
                Not now
              </button>
            </motion.div>
          ) : (
            <motion.form key="loginForm" onSubmit={handleSubmit} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <div style={{ marginBottom: 16 }}>
                <label style={S.label}>Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={form.email}
                  onChange={handleChange}
                  style={S.input}
                  required
                />
              </div>

              <div style={{ marginBottom: 22 }}>
                <label style={S.label}>Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Enter your password"
                    value={form.password}
                    onChange={handleChange}
                    style={{ ...S.input, paddingRight: 60 }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", fontSize: 12, fontWeight: 600, color: C.muted, cursor: "pointer" }}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading} style={{ ...S.primaryBtn, opacity: loading ? 0.7 : 1 }}>
                {loading ? "Logging in…" : "Log in"}
              </button>

              {((bioReady && webAuthnOk) || nativeBio) && (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
                    <div style={{ flex: 1, height: 1, background: C.border }} />
                    <span style={{ fontSize: 11, color: C.muted, fontWeight: 600, letterSpacing: "0.1em" }}>OR</span>
                    <div style={{ flex: 1, height: 1, background: C.border }} />
                  </div>
                  <motion.button
                    type="button"
                    onClick={handleBiometricLogin}
                    disabled={bioLoading}
                    whileTap={{ scale: 0.98 }}
                    style={{ ...S.bioBtn, opacity: bioLoading ? 0.7 : 1 }}
                  >
                    <FingerprintIcon />
                    {bioLoading ? "Waiting for your device…" : "Log in with fingerprint / face"}
                  </motion.button>
                </>
              )}

              <div style={{ textAlign: "center", marginTop: 20 }}>
                <button type="button" onClick={() => setShowSwitchConfirm(true)} style={{ background: "none", border: "none", fontSize: 12, color: C.muted, textDecoration: "underline", cursor: "pointer" }}>
                  Wrong country? Switch
                </button>
              </div>

              <div style={{ textAlign: "center", fontSize: 13, color: C.muted, marginTop: 16 }}>
                Don't have an account?{" "}
                <button type="button" onClick={() => navigate("/register")} style={{ background: "none", border: "none", color: C.gold, fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
                  Register
                </button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Wrong country sheet */}
      <AnimatePresence>
        {wrongCountryModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 100 }}>
            <motion.div initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }} style={S.sheet}>
              <div style={{ width: 40, height: 4, background: "rgba(255,255,255,0.15)", borderRadius: 4, margin: "0 auto 20px" }} />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>Wrong country</div>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 6 }}>
                  This account belongs to <span style={{ color: C.text }}>{wrongCountryModal.accountCountry} {wrongCountryModal.accountCountryName}</span>
                </div>
                <div style={{ background: "rgba(184,147,95,0.08)", border: "1px solid rgba(184,147,95,0.25)", borderRadius: 10, padding: "12px 14px", fontSize: 13, color: C.gold, lineHeight: 1.6, marginBottom: 20 }}>
                  You're on the <strong>{country.code} {country.name}</strong> side. Switch to log in to this account.
                </div>
                <button onClick={handleSwitchToCorrectCountry} style={{ ...S.primaryBtn, marginBottom: 10 }}>
                  Switch to {wrongCountryModal.accountCountry} {wrongCountryModal.accountCountryName}
                </button>
                <button onClick={() => setWrongCountryModal(null)} style={S.ghostBtn}>Cancel</button>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 14 }}>
                  Each account is tied to the country where it was created
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Switch country confirm sheet */}
      <AnimatePresence>
        {showSwitchConfirm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 100 }}>
            <motion.div initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }} style={S.sheet}>
              <div style={{ width: 40, height: 4, background: "rgba(255,255,255,0.15)", borderRadius: 4, margin: "0 auto 20px" }} />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>Switch country?</div>
                <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
                  You'll be taken back to the country selector to choose a different country.
                </div>
                <button onClick={handleConfirmSwitchCountry} style={{ ...S.primaryBtn, marginBottom: 10 }}>
                  Yes, switch country
                </button>
                <button onClick={() => setShowSwitchConfirm(false)} style={S.ghostBtn}>Cancel</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
